// React dependencies
import React, { Fragment, useEffect, useState } from 'react';

// External dependencies
import { IconButton } from '@material-ui/core';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import moment from 'moment';
import { connect } from 'react-redux';

// Internal dependencies
import Colours from '../../../../styles/colours';
import MText from '../../../../components/Common/MText';
import Icons from '../../../../components/Common/Icons';
import PoiStage from './Poi';
import { IsEmpty } from '../../../../common/checks';
import SelectPhotos from '../../../../common/selectPhotos';
import PhotoStage from './Photos';
import ReviewStage from './Review';
import Loading from '../../../../components/Loading';
import ApiService from '../../../../services/apiService';
import { MAX_FILE_SIZE, MAX_FILES } from './SnapstyrConstants';

const ProcessStage = {
  Poi:    'Place of interest',
  Photos: 'Photos',
  Review: 'Review',
};

const PoiSubStage = {
  Name:     'Name',
  Location: 'Location',
  Others:   'Others',
};

const SnapstyrAdd = (props) => {
  const [currentStage, setCurrentStage] = useState(ProcessStage.Poi);
  const [poiSubStage, setPoiSubStage] = useState(PoiSubStage.Name);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [photos, setPhotos] = useState([]);

  const [poiData, setPoiData] = useState({
    proposedName:    '',
    selectedPoi:     null,
    address:         null,
    isCustomAddress: false,
    tags:            [],
    description:     '',
    hours:           null,
  });

  const onUpdatePoiData = (name, value) => {
    setPoiData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const onSelectPhotos = async () => {
    try {
      const files = await SelectPhotos({
        isMultiple:         true,
        maxFiles:           IsEmpty(photos) ? MAX_FILES : MAX_FILES - photos.length,
        maxFileSize:        MAX_FILE_SIZE,
        onMaxFilesExceeded: () => {
          toast.info(`You can only upload a maximum of ${MAX_FILES} photos`);
        },
        onMaxFileSizeExceeded: () => {
          toast.info(`There is a limit of ${MAX_FILE_SIZE}mb per photo`);
        },
      });

      if (!IsEmpty(files)) {
        setPhotos((prev) => [...prev, ...(files.map((f) => ({
          url:         f.url,
          file:        f.file,
          location:    null,
          takenOn:     null,
          description: '',
          tags:        [],
        })))]);
        setCurrentStage(ProcessStage.Photos);
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const onPhotosChange = (newPhotos) => {
    setPhotos(newPhotos);
  };

  const onSubmit = async () => {
    try {
      setIsSubmitting(true);
      const photosMetadata = photos.map((photo) => ({
        takenOn:   !IsEmpty(photo.takenOn) ? moment(photo.takenOn).utcOffset('+08').format('YYYY-MM-DD') : null,
        text:      photo.description,
        lat:       photo.location?.lat || null,
        lng:       photo.location?.lng || null,
        photoTags: photo.tags || [],
      }));

      const formData = new FormData();
      for (const photo of photos) {
        formData.append('photos', photo.file);
      }

      formData.append('photosMetadata', JSON.stringify(photosMetadata));

      if (!IsEmpty(poiData.selectedPoi)) {
        formData.append('poiId', poiData.selectedPoi.id);
      } else {
        formData.append('poi', JSON.stringify({
          name:        poiData.proposedName,
          tags:        poiData.tags,
          description: poiData.description,
          address:     poiData.address.name ?? null,
          lat:         poiData.address.lat,
          lng:         poiData.address.lng,
          hours:       poiData.hours ?? null,
        }));
      }

      await ApiService.uploadPoiPhotos(props.selectedPage.id, formData);
      toast.success('Photos uploaded');
      props.onComplete();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    document.body.style.overflow = props.open ? 'hidden' : '';
    if (!props.open) {
      setCurrentStage(ProcessStage.Poi);
      setPoiSubStage(PoiSubStage.Name);
      setIsSubmitting(false);
      setPhotos([]);
      setPoiData({
        proposedName:    '',
        selectedPoi:     null,
        address:         null,
        isCustomAddress: false,
        tags:            [],
        description:     '',
        hours:           null,
      });
    }
    return () => { document.body.style.overflow = ''; };
  }, [props.open]);

  if (!props.open) {
    return null;
  }

  return (
    <>
      {isSubmitting && <Loading />}
      <div
        className="container"
        style={{
          minHeight:       '100vh',
          backgroundColor: Colours.White,
        }}
      >
        <div className="row">
          <div className="col-12 d-flex flex-column">
            <div className="pl-1 pl-lg-3 pr-1 mt-3 d-flex flex-row align-items-center justify-content-between">
              <MText
                size={14}
                colour={Colours.Gray8}
                text="Snapstyr Photos Upload"
              />

              <IconButton
                onClick={props.onClose}
              >
                <Icons.Cross
                  colour={Colours.Black}
                  fontSize="1rem"
                />
              </IconButton>
            </div>

            <div
              className="pl-1 pl-lg-3 d-flex flex-row align-items-center"
              style={{ gap: 24 }}
            >
              {
                  Object.values(ProcessStage).map((stageName, idx) => (
                    <Fragment key={stageName}>
                      <MText
                        size={12}
                        colour={stageName === currentStage ? Colours.Black : Colours.LightGray4}
                        semibold={stageName === currentStage}
                        text={stageName}
                      />
                      {idx !== Object.values(ProcessStage).length - 1 && (
                        <Icons.ChevronRight />
                      )}
                    </Fragment>
                  ))
              }
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-xl-2" />

          <div className="col-12 col-xl-8 px-3 px-xl-0">
            {
              currentStage === ProcessStage.Poi && (
                <PoiStage
                  poiData={poiData}
                  currentSubStage={poiSubStage}
                  onSubStageChange={(subStage) => setPoiSubStage(subStage)}
                  onUpdatePoiData={onUpdatePoiData}
                  onCompleteForm={() => {
                    if (!IsEmpty(photos)) {
                      setCurrentStage(ProcessStage.Photos);
                    } else {
                      onSelectPhotos();
                    }
                  }}
                  onScrollToTop={props.onScrollToTop}
                />
              )
            }
            {
              currentStage === ProcessStage.Photos && (
                <PhotoStage
                  photos={photos}
                  poiName={!IsEmpty(poiData.selectedPoi) ? poiData.selectedPoi.name : poiData.proposedName}
                  onPhotosChange={onPhotosChange}
                  poiAddress={!IsEmpty(poiData.selectedPoi) ? {
                    name: poiData.selectedPoi.address,
                    lat:  poiData.selectedPoi.lat,
                    lng:  poiData.selectedPoi.lng,
                  } : poiData.address}
                  onStageComplete={() => setCurrentStage(ProcessStage.Review)}
                  onBackToPreviousStage={() => setCurrentStage(ProcessStage.Poi)}
                  onScrollToTop={() => { props.onScrollToTop(); }}
                />
              )
            }
            {
              currentStage === ProcessStage.Review && (
                <ReviewStage
                  selectedPoi={poiData.selectedPoi}
                  poiName={!IsEmpty(poiData.selectedPoi) ? poiData.selectedPoi.name : poiData.proposedName}
                  isCustomAddress={poiData.isCustomAddress}
                  address={!IsEmpty(poiData.selectedPoi) ? {
                    name: poiData.selectedPoi.address,
                    lat:  poiData.selectedPoi.lat,
                    lng:  poiData.selectedPoi.lng,
                  } : poiData.address}
                  description={!IsEmpty(poiData.selectedPoi) ? poiData.selectedPoi.description : poiData.description}
                  hours={!IsEmpty(poiData.selectedPoi) ? poiData.selectedPoi.hours : poiData.hours}
                  tags={!IsEmpty(poiData.selectedPoi) ? poiData.selectedPoi.tags : poiData.tags}
                  photos={photos}
                  onNavigateToPreviousStage={() => setCurrentStage(ProcessStage.Photos)}
                  onSubmit={onSubmit}
                />
              )
            }
          </div>
        </div>

        <div className="row" style={{ height: '15vh' }} />
      </div>
    </>
  );
};

SnapstyrAdd.propTypes = {
  open:         PropTypes.bool.isRequired,
  onClose:      PropTypes.func.isRequired,
  selectedPage: PropTypes.shape({
    id: PropTypes.number.isRequired,
  }).isRequired,
  onComplete:    PropTypes.func.isRequired,
  onScrollToTop: PropTypes.func,
};

SnapstyrAdd.defaultProps = {
  onScrollToTop: () => {},
};

const mapStateToProps = (state) => ({
  selectedPage: state.page.selected,
});

export default connect(mapStateToProps, null)(SnapstyrAdd);
